import { getPublisherParams } from './publisherParams'
import { applyUrlPassthrough } from './urlPassthrough'
import { getWebLandingURL } from './landingUrl'
import { resolveSub3 } from './metaCookies'

// Builds the Media-Matchers Clicks Search redirect URL used by the
// /explore/{brand}-{version} bridge pages.
//
// Any lead data already present on the bridge page's own URL is forwarded,
// using the SAME accepted parameters and value mappings as the
// /request-information/v1 form: each answer can arrive either as the form's
// own value (e.g. highestEducation=some-college) or as the numeric
// Media-Matchers code (e.g. highestEducation=5). Everything is normalized to
// the numeric code before being sent.
const EDUCATION_TO_CODE: Record<string, string> = {
  'some-high-school': '2',
  'high-school': '1',
  'some-college': '5',
  associates: '5',
  bachelors: '7',
  masters: '7',
  doctorate: '7',
}
const EDUCATION_CODES = ['1', '2', '5', '7', '9']
const START_TIMEFRAME_TO_CODE: Record<string, string> = {
  immediately: '1',
  '1-3-months': '2',
  '3-6-months': '3',
  '6-12-months': '4',
  '1-year-plus': '4',
  undecided: '6',
}
const START_TIMEFRAME_CODES = ['1', '2', '3', '4', '5', '6']
// None=1, Active Duty=2, Veteran=3, National Guard=4, Spouse=5, Dependent=6.
// "Unknown" has no code and is sent blank.
const MILITARY_TO_CODE: Record<string, string> = {
  none: '1',
  'active-duty': '2',
  veteran: '3',
  guard: '4',
  'national-guard': '4',
  spouse: '5',
  dependent: '6',
}
const MILITARY_CODES = ['1', '2', '3', '4', '5', '6']
const normalizeCode = (
  raw: string | null,
  byFormValue: Record<string, string>,
  validCodes: string[],
): string => {
  if (!raw) return ''
  const value = raw.trim().toLowerCase()
  if (!value) return ''
  if (validCodes.includes(value)) return value
  return byFormValue[value] || ''
}
const normalizeGradYear = (raw: string | null): string => {
  if (!raw) return ''
  const value = raw.trim().toLowerCase()
  // The dropdown's last option, "1989 or earlier", posts 1989 — so anything
  // earlier than 1990 (including the legacy "before-1965" spelling) collapses
  // to that value.
  if (value === 'before-1965' || value === 'before-1990') return '1989'
  const year = Number(value)
  const currentYear = new Date().getFullYear()
  if (!Number.isInteger(year) || year < 1900 || year > currentYear) return ''
  return year < 1990 ? '1989' : String(year)
}
const normalizeNumber = (raw: string | null): string => {
  if (!raw) return ''
  const value = raw.trim()
  return /^\d+$/.test(value) ? value : ''
}
export interface ClicksSearchOptions {
  // Used when the incoming URL supplies neither mPubGroupID nor the
  // mPubID + mPubCampaignID pair.
  defaultPubGroupID: string
  // Optional advertiser brand id for this bridge page.
  mBrandID?: string
  // Optional program id sent when the incoming URL doesn't supply one.
  defaultMProgram?: string
  // Optional degree level / area of study sent when the incoming URL doesn't
  // supply them. Used by the /explore/{degree}-{program} pages, where the
  // page itself declares which combination the visitor clicked on.
  defaultDegreeInterest?: string
  defaultAreaStudy?: string
}
export function buildClicksSearchUrl(
  search: string,
  {
    defaultPubGroupID,
    mBrandID = '',
    defaultMProgram = '',
    defaultDegreeInterest = '',
    defaultAreaStudy = '',
  }: ClicksSearchOptions,
): string {
  const incoming = new URLSearchParams(search)
  const get = (...names: string[]): string => {
    for (const name of names) {
      const value = incoming.get(name)
      if (value && value.trim()) return value.trim()
    }
    return ''
  }
  const params = new URLSearchParams()
  // Blank values are omitted entirely rather than sent as empty keys.
  const add = (name: string, value: string) => {
    if (value) params.set(name, value)
  }
  add(
    'degreeInterest',
    normalizeNumber(incoming.get('degreeInterest')) || defaultDegreeInterest,
  )
  add(
    'areaStudy',
    normalizeNumber(incoming.get('areaStudy') || incoming.get('areaOfStudy')) ||
      defaultAreaStudy,
  )
  add(
    'mProgram',
    normalizeNumber(incoming.get('mProgram')) || defaultMProgram,
  )
  add(
    'highestEducation',
    normalizeCode(
      incoming.get('highestEducation'),
      EDUCATION_TO_CODE,
      EDUCATION_CODES,
    ),
  )
  add('gradYear', normalizeGradYear(incoming.get('gradYear')))
  add(
    'militaryAffiliation',
    normalizeCode(
      incoming.get('militaryAffiliation') || incoming.get('military'),
      MILITARY_TO_CODE,
      MILITARY_CODES,
    ),
  )
  add(
    'startTimeframe',
    normalizeCode(
      incoming.get('startTimeframe'),
      START_TIMEFRAME_TO_CODE,
      START_TIMEFRAME_CODES,
    ),
  )
  add('sub1', get('sub1'))
  add('sub2', get('sub2'))
  // sub3 carries the Meta match keys ("<fbc>_<fbp>") when the Pixel cookies
  // are present, otherwise whatever the incoming URL supplied.
  add('sub3', resolveSub3(search))
  add('zipCode', get('zipCode').replace(/\D/g, '').slice(0, 5))
  params.set('redirect', '1')
  // mPubGroupID, or the mPubID + mPubCampaignID pair, else the page default.
  Object.entries(
    getPublisherParams(search, {
      mPubGroupID: defaultPubGroupID,
    }),
  ).forEach(([key, value]) => add(key, value))
  add('firstName', get('firstName'))
  add('lastName', get('lastName'))
  add('address', get('address', 'streetAddress'))
  add('email', get('email'))
  add('mBrandID', get('mBrandID') || mBrandID)
  add('webLandingURL', getWebLandingURL())
  // Anything else the traffic source appended (utm_*, fbclid, clickid, …) is
  // forwarded as-is; resolved values above always win.
  applyUrlPassthrough(params, search)
  return `https://api.media-matchers.com/search?${params.toString()}`
}
